import React, {useState, useCallback} from "react"
import * as Order from "../../dtos/orderResponse"
import Tax from "../formatting/Tax"
import Price from "../formatting/Price"
import AddressDetails from "./AddressDetails"
import Discount from "../formatting/Discount"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import {ShippingAddress} from "./ShippingAddress"
import {BillingAddress} from "./BillingAddress"
import TooltipLabel from "../toolTip/TooltipLabel"
import {isDeliverableOrder} from "../../utils/isDeliverableOrder"
import {isFullyShippedOrder} from "../../utils/orderStatusHelper"
import {postActionLog, ButtonActionTypes} from "../../services/order/TrackOrderActions"
import {getCaseId} from "../../utils/UrlParser"
import FieldDisplay from "../formatting/FieldDisplay"
import {getFormattedAmount} from "../../utils/amountFormatter"
import {SmsSubscription} from "./SmsSubscription"
import {getBulkyChargeFees, getRetailDeliveryFees} from "../../utils/orderFeesHelper"
import {makeStyles} from "tss-react/mui"
import {CardContent, Grid, IconButton, Link, Typography} from "@mui/material"
import OrderTimeline from "./OrderTimeline"
import {EventResponse} from "../../dtos/eventResponse"
import {getOrderEventsTimeline} from "../../services/order/eventHelper"

const caseId = getCaseId()

interface OrderOverviewProps {
	customerId: string
	order: Order.Order
	isVCSAgent: boolean
	isVCSOrder: boolean
	updateOrder: () => void
	eventResponse: EventResponse | null
	isOrderPaid: boolean | undefined
}

const useStyles = makeStyles()({
	label: {
		fontSize: "12px !important",
	},
	cardContent: {
		paddingLeft: "30px",
		paddingRight: "50px",
	},
})

export default function OrderOverview(props: OrderOverviewProps) {
	const {classes} = useStyles()
	const order = props.order
	const [editShippingAddress, setEditShippingAddress] = useState(false)
	const [editBillingAddress, setEditBillingAddress] = useState(false)
	const orderDate = new Date(order ? order.orderData.orderCreationDate : "").toString()

	function getTotalTax(): number {
		let tax: number = 0
		for (let j = 0; j < order.orderData.lineItems.length; j++) {
			tax += order.orderData.lineItems[j].pricingBreakdownV3.totalDiscountedPriceCents
				? order.orderData.lineItems[j].pricingBreakdownV3.totalDiscountedPriceCents.taxed -
				  order.orderData.lineItems[j].pricingBreakdownV3.totalDiscountedPriceCents.untaxed
				: 0
		}
		tax +=
			(order.orderData.shippingTaxedPrice ? order.orderData.shippingTaxedPrice.centAmount : 0) -
			(order.orderData.shippingPrice ? order.orderData.shippingPrice.centAmount : 0)
		return tax
	}

	function getTotalOrderWithoutDiscount(): number {
		let totalOrderDiscount: number = 0
		for (let index = 0; index < order.orderData.lineItems.length; index++) {
			totalOrderDiscount += order.orderData.lineItems[index].pricingBreakdownV3.totalListPriceCents
				? order.orderData.lineItems[index].pricingBreakdownV3.totalListPriceCents.taxed
				: 0
		}
		totalOrderDiscount += order.orderData.shippingTaxedPrice.centAmount
		return totalOrderDiscount
	}

	/**
	 * If one of items shippingAddressEdit fields is disabled, shipping Address Edit for the order will be disabled
	 */

	const disabledShippingAddressEdit = useCallback(() => {
		if (props.order.isLegacy) return true
		if (props.isVCSOrder && !props.isVCSAgent) return true
		if (isFullyShippedOrder(props.order.orderData)) return true
		const disableShippingAddressEdit = order.orderData.lineItems.reduce((accumulator, current) => {
			return (
				accumulator ||
				(current.customOrderData &&
				current.customOrderData.order &&
				current.customOrderData.order.postOrderActionOverrides &&
				current.customOrderData.order.postOrderActionOverrides.shippingAddressEdit
					? current.customOrderData.order.postOrderActionOverrides.shippingAddressEdit.disabled
					: false)
			)
		}, false)
		return disableShippingAddressEdit
	}, [
		props.isVCSAgent,
		props.isVCSOrder,
		props.order.isLegacy,
		order.orderData.lineItems,
		props.order.orderData,
	])

	const closeEditShipping = () => {
		setEditShippingAddress(false)
	}

	const closeEditBilling = () => {
		setEditBillingAddress(false)
	}
	const disableBillingAddressEdit = useCallback(() => {
		if (props.order.isLegacy) return true
		if (props.isVCSOrder && !props.isVCSAgent) return true
		return !isDeliverableOrder(props.order.orderData.lineItems)
	}, [props.order, props.isVCSAgent, props.isVCSOrder])
	const UpdateBillingAddress = useCallback(
		() => (
			<IconButton
				disabled={disableBillingAddressEdit()}
				style={{padding: 0, paddingLeft: 5}}
				aria-label="Edit"
				aria-haspopup="true"
				onClick={() => {
					logActionToSnowflake(ButtonActionTypes.ClickEditBillingAddressButton)
					setEditBillingAddress(true)
				}}
			>
				<EditOutlinedIcon style={{padding: 0}} />
			</IconButton>
		),
		// eslint-disable-next-line
		[disableBillingAddressEdit]
	)
	const UpdateShippingAddress = useCallback(
		() => (
			<IconButton
				disabled={disabledShippingAddressEdit()}
				style={{padding: 0, paddingLeft: 5}}
				aria-label="Edit"
				aria-haspopup="true"
				onClick={() => {
					logActionToSnowflake(ButtonActionTypes.ClickEditShippingAddressButton)
					setEditShippingAddress(true)
				}}
			>
				<EditOutlinedIcon style={{padding: 0}} />
			</IconButton>
		),
		// eslint-disable-next-line
		[props.order, disabledShippingAddressEdit]
	)

	const logActionToSnowflake = (action: string) => {
		postActionLog(
			{
				event: action,
				actionMetaData: {
					orderNumber: props.order.orderNumber,
				},
			},
			caseId
		)
	}

	return (
		<CardContent className={classes.cardContent}>
			<Grid container spacing={3} justify-content="space-between">
				<Grid item>
					{props.order && props.order.orderNumber && props.order.orderNumber.startsWith("DIGI") ? (
						<div>
							<Typography className={classes.label}>Ordered</Typography>
							<Typography color="textPrimary" style={{fontSize: "14px !important"}}>
								{orderDate}
							</Typography>
						</div>
					) : (
						<OrderTimeline
							orderCreationDate={orderDate}
							orderTimelineDetails={getOrderEventsTimeline(orderDate, props.eventResponse)}
							isOrderPaid={props.isOrderPaid}
							eventResponse={props.eventResponse}
						/>
					)}
				</Grid>
				<Grid item>
					<Grid item>
						<Typography className={classes.label}>Price</Typography>
					</Grid>
					<Grid item>
						<Typography className={classes.label} variant="caption">
							<Tax
								taxedValue={getTotalTax()}
								untaxedValue={0}
								fractionDigits={order.orderData.shippingPrice.fractionDigits}
								currency={order.orderData.shippingPrice.currencyCode}
							/>
						</Typography>
					</Grid>
					<Grid item>
						<Typography
							variant="subtitle1"
							display="block"
							color="textPrimary"
							style={{fontWeight: "bold", fontSize: "13px"}}
						>
							{`Total order: `}
							<Price
								value={
									order.orderData.orderTotal.centAmount
										? String(order.orderData.orderTotal.centAmount)
										: "0"
								}
								fractionDigits={order.orderData.orderTotal.fractionDigits}
								currency={order.orderData.orderTotal.currencyCode}
							/>
							*
						</Typography>
					</Grid>
					{getTotalOrderWithoutDiscount() === order.orderData.orderTotal.centAmount ? (
						<></>
					) : (
						<Grid item>
							<Typography className={classes.label}>
								{!order.isLegacy && (
									<Discount
										originalValue={getTotalOrderWithoutDiscount()}
										discountedValue={order.orderData.orderTotal.centAmount}
										fractionDigits={order.orderData.shippingPrice.fractionDigits}
										currency={order.orderData.shippingPrice.currencyCode}
										pricingQuoteInfo={order.orderData.pricingQuoteInfo}
									/>
								)}
							</Typography>
						</Grid>
					)}
					<Typography
						style={
							order.orderData.payments != null &&
							order.orderData.payments.length > 0 &&
							(order.orderData.payments[0].paymentMethod || "").toLowerCase() === "multibanco"
								? {
										fontWeight: "bold",
										color: "gray",
										fontSize: "11px",
										display: "inline-block",
								  }
								: {display: "none"}
						}
					>
						Payment state: Deferred Payment
					</Typography>
				</Grid>
				{props.isVCSOrder && (
					<Grid item>
						<Typography className={classes.label}>VCS</Typography>
						<FieldDisplay label="Tenant Name" data={order.orderData.tenantId} />
						{order &&
							order.orderData &&
							order.orderData.customOrderData &&
							order.orderData.customOrderData.site &&
							order.orderData.customOrderData.site.vcsStoreName && (
								<FieldDisplay
									label="Store Name"
									data={order.orderData.customOrderData.site.vcsStoreName}
								/>
							)}
						{order &&
							order.orderData &&
							order.orderData.customOrderData &&
							order.orderData.customOrderData.site &&
							order.orderData.customOrderData.site.vcsStoreUrl && (
								<>
									<Grid container direction="row">
										<Grid item>
											<Typography
												variant="subtitle2"
												color="textSecondary"
												align="left"
												component="p"
											>
												Store Url
											</Typography>
										</Grid>
										<div style={{width: "5px"}}></div>
										<Grid item>
											<Link
												sx={{textDecoration: "none", color: "black"}}
												href={`${order.orderData.customOrderData.site.vcsStoreUrl}`}
												target="_blank"
												underline="none"
											>
												<Typography style={{fontSize: "12px !important"}}>
													{order.orderData.customOrderData.site.vcsStoreUrl}
												</Typography>
											</Link>
										</Grid>
									</Grid>
								</>
							)}
					</Grid>
				)}
				<Grid item>
					<Typography className={classes.label}>Shipping details</Typography>
					<Typography color="textPrimary" component="div">
						<div>
							{order.orderData.orderShippingData.name && (
								<FieldDisplay
									label="Method"
									data={`${order?.orderData?.orderShippingData?.name}`}
								/>
							)}
							{
								<TooltipLabel
									toolTipHintText={`For orders with multiple items, this date is for the first item's arrival. Please refer to the specific item for its estimated arrival date`}
									labelTitle=""
								>
									<FieldDisplay
										label="Estimated arrival"
										data={`${order.orderData.orderShippingData.earliestArrivalDate}`}
									/>
								</TooltipLabel>
							}
							{order.orderData && order.orderData.shippingTaxedPrice && (
								<FieldDisplay
									label="Shipping"
									data={`${getFormattedAmount({
										amount: order.orderData.shippingTaxedPrice.centAmount || 0,
										fractionDigits: order.orderData.shippingTaxedPrice.fractionDigits || 2,
										currency: order.orderData.shippingTaxedPrice.currencyCode || "",
									})}*`}
								/>
							)}
							{getRetailDeliveryFees(props.order) !== 0 && (
								<FieldDisplay
									label="Retail delivery fees"
									data={`${getFormattedAmount({
										amount: getRetailDeliveryFees(props.order),
										fractionDigits: order.orderData.shippingTaxedPrice.fractionDigits || 2,
										currency: order.orderData.shippingTaxedPrice.currencyCode || "",
									})}*`}
								/>
							)}
							{getBulkyChargeFees(props.order) !== 0 && (
								<FieldDisplay
									label="Bulky item charges"
									data={`${getFormattedAmount({
										amount: getBulkyChargeFees(props.order),
										fractionDigits: order.orderData.shippingTaxedPrice.fractionDigits || 2,
										currency: order.orderData.shippingTaxedPrice.currencyCode || "",
									})}*`}
								/>
							)}
						</div>
					</Typography>
					<SmsSubscription orderId={props.order.orderNumber} />
				</Grid>
				<Grid item>
					<Typography className={classes.label}>
						{!disabledShippingAddressEdit() ? (
							<Typography className={classes.label}>
								Shipping Address
								{UpdateShippingAddress()}
							</Typography>
						) : (
							<TooltipLabel
								toolTipHintText={`${
									props.order.isLegacy
										? "Cannot update the shipping address for legacy orders! Please use the CCT tool."
										: "The shipping address for this order cannot be updated"
								}`}
								labelTitle="Shipping Address"
							>
								{UpdateShippingAddress()}
							</TooltipLabel>
						)}
					</Typography>

					<Typography color="textPrimary" component="div">
						<AddressDetails address={order.orderData.shippingAddress} />
					</Typography>
				</Grid>
				<Grid item>
					{disableBillingAddressEdit() ? (
						<TooltipLabel
							toolTipHintText={`${
								props.order.isLegacy
									? "Cannot update the billing address for legacy orders! Please use the CCT tool."
									: "The billing address for this order cannot be updated"
							}`}
							labelTitle="Billing Address"
						>
							{UpdateBillingAddress()}
						</TooltipLabel>
					) : (
						<Typography className={classes.label}>
							Billing Address
							{UpdateBillingAddress()}
						</Typography>
					)}

					<Typography color="textPrimary" component="div">
						<AddressDetails address={order.orderData.billingAddress} />
					</Typography>
				</Grid>
			</Grid>
			<ShippingAddress
				open={editShippingAddress}
				close={closeEditShipping}
				order={order}
				reloadParent={props.updateOrder}
			/>
			<BillingAddress
				open={editBillingAddress}
				close={closeEditBilling}
				order={order}
				reloadParent={props.updateOrder}
			/>
			<Typography style={{fontWeight: 300, color: "gray", fontSize: "11px"}} component="div">
				*Taxes and fees included
			</Typography>
		</CardContent>
	)
}
